import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'wouter';
import { 
  FileText, 
  BarChart3, 
  Settings, 
  Database, 
  TrendingUp, 
  Search,
  Building2,
  Globe,
  Users,
  Award,
  Clock,
  AlertCircle,
  CheckCircle2,
  Activity,
  RefreshCw,
  Zap,
  Layout,
  BarChart2,
  Timer,
  Target
} from 'lucide-react';
import { Workflow, Monitor } from "lucide-react";
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../components/Toast';
import './ClientLandingPage.css';

interface Client {
  id: number;
  name: string;
  code: string;
  description?: string;
  website?: string;
  logoUrl?: string;
  logoBase64?: string;
  brandPrimaryColor?: string;
  brandSecondaryColor?: string;
  brandAccentColor?: string;
  industry?: string;
  businessModel?: string;
  companySize?: string;
  headquartersCountry?: string;
  headquartersState?: string;
  headquartersCity?: string;
  serviceTier?: string;
  supportLevel?: string;
  currentHealthScore?: number;
  totalDatabases?: number;
  totalQueriesAnalyzed?: number;
  criticalIssuesCount?: number;
  lastHealthCheck?: string;
  status?: string;
  isActive?: boolean;
  accountManager?: string;
  solutionArchitect?: string;
  tags?: string[];
}

interface SystemMetrics {
  healthScore: number;
  databases: number;
  queriesAnalyzed: number;
  criticalIssues: number;
  lastCheck: string;
  uptime: string;
  performanceScore: number;
}

interface QuickAction {
  id: string;
  title: string;
  description: string;
  icon: any;
  path: string;
  color: string;
}

export default function ClientLandingPage() {
  //console.error('🚨🚨🚨 CLIENTLANDINGPAGE IS RUNNING 🚨🚨🚨'); // Make it an error so it shows in red
 //alert('ClientLandingPage is running!');
  
  const { clientCode } = useParams<{ clientCode: string }>();
  const [location, navigate] = useLocation();
  const { theme } = useTheme();
  const { addToast } = useToast();
  
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [systemMetrics, setSystemMetrics] = useState<SystemMetrics | null>(null);

  // Quick Actions Configuration - Organized by Category
  const quickActionCategories = [
    {
      title: 'Analysis & Assessment',
      description: 'Analyze system architecture, code, and database structure',
      actions: [
        {
          id: 'arch-analysis',
          title: 'Architecture Analysis',
          description: 'Create and analyze system architecture diagrams',
          icon: Workflow,
          path: `/clients/${clientCode}/arch-analysis`,
          color: 'bg-emerald-500'
        },
        {
          id: 'VM-analysis',
          title: 'VM Analysis',
          description: 'Capture VM profile and performance metrics',
          icon: Monitor,
          path: `/clients/${clientCode}/vm-profile`,
          color: 'bg-emerald-600'
        },
        {
          id: 'schema-analysis',
          title: 'Schematic Analysis',
          description: 'Analyze database schema and structure',
          icon: Layout,
          path: `/clients/${clientCode}/sql/database-design`,
          color: 'bg-emerald-700'
        },
        {
          id: 'sql-analyzer',
          title: 'Static Code Analysis',
          description: 'Analyze and optimize SQL queries',
          icon: Search,
          path: `/clients/${clientCode}/static-analysis`,
          color: 'bg-teal-500'
        },
        {
          id: 'index-analysis',
          title: 'Index Analysis',
          description: 'Analyze and optimize indexes',
          icon: Database,
          path: `/clients/${clientCode}/static-analysis`,
          color: 'bg-teal-600'
        }
      ]
    },
    {
      title: 'Health & Diagnostics',
      description: 'Monitor system health and run diagnostic tools',
      actions: [
        {
          id: 'health-dashboard',
          title: 'Baseline Health Dashboard',
          description: 'View detailed system health metrics',
          icon: Activity,
          path: `/clients/${clientCode}/diagnostics`,
          color: 'bg-green-500'
        },
        {
          id: 'diagnostic-scripts',
          title: 'Diagnostic Scripts',
          description: 'Run system diagnostic scripts',
          icon: Settings,
          path: `/clients/${clientCode}/diagnostics`,
          color: 'bg-green-600'
        }
      ]
    },
    {
      title: 'Performance & Testing',
      description: 'Test performance, stability, and optimization',
      actions: [
        {
          id: 'load-testing',
          title: 'Load Testing',
          description: 'Perform load testing and stress analysis',
          icon: Timer,
          path: `/clients/${clientCode}/load-testing`,
          color: 'bg-shiftleft-500'
        },
        {
          id: 'stabilize',
          title: 'Stabilize',
          description: 'Performance testing, stress testing, and system stabilization',
          icon: Target,
          path: `/clients/${clientCode}/stabilize`,
          color: 'bg-shiftleft-600'
        },
        {
          id: 'performance-tuning',
          title: 'Performance Tuning',
          description: 'Optimize database performance',
          icon: TrendingUp,
          path: `/clients/${clientCode}/bacpac-assessment-report`,
          color: 'bg-shiftleft-700'
        }
      ]
    },
    {
      title: 'Operations',
      description: 'Manage database operations and failover procedures',
      actions: [
        {
          id: 'failover',
          title: 'Failover',
          description: 'Manage database failover operations',
          icon: RefreshCw,
          path: `/clients/${clientCode}/failover`,
          color: 'bg-orange-500'
        }
      ]
    }
  ];
  // Debug: Log categorized actions
  console.log('Quick Action Categories:', quickActionCategories.length);
  console.log('Total Actions:', quickActionCategories.reduce((total, cat) => total + cat.actions.length, 0));
  // Fetch client data
  useEffect(() => {
    if (!clientCode) return;

    const fetchClientData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Mock client data for development
        const clientData = {
          id: 1,
          name: clientCode === 'COSTCO' ? 'Costco Wholesale Corporation' : clientCode.charAt(0) + clientCode.slice(1).toLowerCase(),
          code: clientCode,
          description: 'Leading membership-only warehouse club',
          logoUrl: `/logos/${clientCode.toLowerCase()}_logo.jpeg`,
          brandPrimaryColor: '#0073e6',
          brandSecondaryColor: '#004c99',
          industry: 'Retail',
          serviceTier: 'Enterprise',
          currentHealthScore: 87,
          totalDatabases: 15,
          totalQueriesAnalyzed: 52340,
          criticalIssuesCount: 2,
          lastHealthCheck: new Date().toISOString(),
          status: 'active',
          accountManager: 'Jishnu Nath',
          solutionArchitect: 'Kiro AI Assistant'
        };
        setClient(clientData);

        // Set system metrics (mock data for now)
        setSystemMetrics({
          healthScore: clientData.currentHealthScore || 85,
          databases: clientData.totalDatabases || 12,
          queriesAnalyzed: clientData.totalQueriesAnalyzed || 45230,
          criticalIssues: clientData.criticalIssuesCount || 3,
          lastCheck: clientData.lastHealthCheck || new Date().toISOString(),
          uptime: '99.8%',
          performanceScore: 88
        });

        // Apply brand colors if available
        if (clientData.brandPrimaryColor) {
          document.documentElement.style.setProperty('--client-primary', clientData.brandPrimaryColor);
        }
        if (clientData.brandSecondaryColor) {
          document.documentElement.style.setProperty('--client-secondary', clientData.brandSecondaryColor);
        }
        if (clientData.brandAccentColor) {
          document.documentElement.style.setProperty('--client-accent', clientData.brandAccentColor);
        }

      } catch (err) {
        console.error('Error fetching client data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load client data');
        addToast('Failed to load client data', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchClientData();
  }, [clientCode, addToast]);

  // Handle quick action clicks
  const handleQuickAction = (action: QuickAction) => {
    addToast(`Opening ${action.title}...`, 'info');
    navigate(action.path);
  };

  // Get health score color
  const getHealthScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-500';
    if (score >= 70) return 'text-yellow-500';
    return 'text-red-500';
  };

  // Get health score status
  const getHealthScoreStatus = (score: number) => {
    if (score >= 90) return 'Excellent';
    if (score >= 70) return 'Good';
    if (score >= 50) return 'Fair';
    return 'Poor';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-shiftleft-600"></div>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Client Not Found
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {error || `Client "${clientCode}" could not be found`}
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-shiftleft-600 text-white rounded-lg hover:bg-shiftleft-700 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="bg-gray-50 dark:bg-gray-900 no-vertical-scroll"
  style={{marginTop:0, paddingTop:0, overflowX:'hidden'}}>
      {/* Header Section */}
      <div 
        className="client-header"
        style={{marginTop: '-74px', paddingTop: 0, marginBottom: 0, borderTop: 0}}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" style={{marginTop:0, paddingTop:0, marginBottom:0, borderTop:0}}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {(client.logoUrl || client.logoBase64) && (
                <img
                  src={client.logoUrl || `data:image/png;base64,${client.logoBase64}`}
                  alt={`${client.name} logo`}
                  className="h-16 w-16 object-contain rounded-lg bg-white p-2"
                />
              )}
              <div>
                <h1 className="text-3xl font-bold text-white">{client.name}</h1>
                <p className="text-shiftleft-100">Client Code: {client.code}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-shiftleft-100 text-sm">Service Tier</div>
              <div className="text-white font-semibold">
                {client.serviceTier || 'Standard'}
              </div>
            </div>
          </div>
        </div>
      </div>

  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-2 pb-8">
        {/* System Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Activity className={`h-8 w-8 ${systemMetrics ? getHealthScoreColor(systemMetrics.healthScore) : 'text-gray-400'}`} />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Health Score
                  </dt>
                  <dd className="flex items-baseline">
                    <div className={`text-2xl font-semibold ${systemMetrics ? getHealthScoreColor(systemMetrics.healthScore) : 'text-gray-900 dark:text-white'}`}>
                      {systemMetrics?.healthScore || 0}%
                    </div>
                    <div className="ml-2 flex items-baseline text-sm font-semibold text-gray-600 dark:text-gray-400">
                      {systemMetrics ? getHealthScoreStatus(systemMetrics.healthScore) : 'Unknown'}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Database className="h-8 w-8 text-shiftleft-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Databases
                  </dt>
                  <dd className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {systemMetrics?.databases?.toLocaleString() || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BarChart3 className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Queries Analyzed
                  </dt>
                  <dd className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {systemMetrics?.queriesAnalyzed?.toLocaleString() || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Critical Issues
                  </dt>
                  <dd className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {systemMetrics?.criticalIssues || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions - Categorized */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Quick Actions</h2>
          
          {quickActionCategories.map((category, categoryIndex) => (
            <div key={categoryIndex} className="mb-8">
              {/* Category Header */}
              <div className="mb-4">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-1">
                  {category.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {category.description}
                </p>
              </div>
              
              {/* Category Actions Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
                {category.actions.map((action) => {
                  const IconComponent = action.icon;
                  return (
                    <div
                      key={action.id}
                      onClick={() => handleQuickAction(action)}
                      className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 cursor-pointer hover:shadow-lg hover:scale-105 transition-all duration-200 border border-gray-200 dark:border-gray-700"
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`flex-shrink-0 ${action.color} rounded-lg p-2.5`}>
                          <IconComponent className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                            {action.title}
                          </h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                            {action.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Category Separator */}
              {categoryIndex < quickActionCategories.length - 1 && (
                <div className="border-b border-gray-200 dark:border-gray-700"></div>
              )}
            </div>
          ))}
        </div>


        {/* Tags */}
        {client.tags && client.tags.length > 0 && (
          <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Tags</h3>
            <div className="flex flex-wrap gap-2">
              {client.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-shiftleft-100 dark:bg-shiftleft-900 text-shiftleft-800 dark:text-shiftleft-200 rounded-full text-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}